import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  FormLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  useUpdateUserProfile,
  useUserProfile,
  useV1ListCurrencies,
  V1ListCurrenciesTypeEnum,
} from "@rentablo/api/apiv1";
import { SettingsLayout } from "@features/settings/components/SettingsLayout";
import { DEFAULT_CURRENCY } from "@constants";

export const SettingsCurrencyPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data: userProfile } = useUserProfile();
  const { mutate: updateUserProfile, isPending: isUpdating } =
    useUpdateUserProfile();
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(
    undefined
  );
  const currentCurrency =
    selectedCurrency ?? userProfile?.reportingCurrency ?? DEFAULT_CURRENCY;

  const { data, isLoading } = useV1ListCurrencies({
    type: [V1ListCurrenciesTypeEnum.STANDARD],
  });

  const currencies = (data?.currencies ?? []).filter(
    (currency): currency is { code: string; name: string } =>
      Boolean(currency.code && currency.name)
  );

  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelectedCurrency(event.target.value);
  };

  const handleSave = () => {
    if (!userProfile?.id) return;
    updateUserProfile(
      {
        id: Number(userProfile.id),
        createOrUpdateProfileParams: {
          reportingCurrency: currentCurrency,
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t("settings.currency.saveSuccess"), {
            variant: "success",
            autoHideDuration: 3000,
          });
        },
      }
    );
  };

  return (
    <SettingsLayout
      breadcrumbs={[
        { text: t("settings.sidebar.title") },
        { text: t("settings.sidebar.common.currency") },
      ]}
      title={t("settings.currency.title")}
    >
      <Box>
        <Typography variant="body3" color="textSecondary" mb={5}>
          {t("settings.currency.description")}
        </Typography>
        <Box mb={5} maxWidth="320px">
          <FormLabel htmlFor="display-currency">
            <Typography variant="body3" mb={2} component="div">
              {t("settings.currency.form.label")}
            </Typography>
          </FormLabel>
          <Select
            id="display-currency"
            fullWidth
            variant="outlined"
            value={currentCurrency}
            onChange={handleChange}
            disabled={isLoading}
            sx={{
              height: 36,
              "& .MuiSelect-select": { padding: "8px 16px" },
            }}
          >
            {currencies.map((currency) => (
              <MenuItem key={currency.code} value={currency.code}>
                {currency.name} ({currency.code})
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Button
          variant="primary"
          size="small"
          onClick={handleSave}
          disabled={isLoading || isUpdating}
        >
          {t("settings.currency.saveButton")}
        </Button>
      </Box>
    </SettingsLayout>
  );
};
